// src/user/StripeKYCVerification.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  Divider,
} from '@mui/material';
import {
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { API } from '../config/apiConfig';
import DashboardPageHeader from '../components/DashboardPageHeader';

const steps = [
  'Create Account',
  'Verify Identity',
  'Add Payment Details',
  'Complete Verification',
];

function StripeKYCVerification() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // KYC Status
  const [kycStatus, setKycStatus] = useState({
    verified: false,
    status: 'not_started',
    chargesEnabled: false,
    detailsSubmitted: false,
    requirements: {
      currentlyDue: [],
      pastDue: [],
      errors: [],
    },
  });

  // Stripe Connect
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [accountCreateLoading, setAccountCreateLoading] = useState(false);
  const [accountLinkUrl, setAccountLinkUrl] = useState(null);

  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
  
  // Get userId from token
  const getUserIdFromToken = () => {
    try {
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.UserId || payload.userId || payload.id || payload.sub || null;
    } catch (error) {
      console.warn('Could not decode token for userId:', error);
      return null;
    }
  };
  
  const userId = getUserIdFromToken();
  const authHeader = { 'Authorization': `Bearer ${token}` };

  const getUserEmailFromToken = () => {
    try {
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.email || payload.Email || null;
    } catch (error) {
      console.warn('Could not decode token for email:', error);
      return null;
    }
  };

  // Fetch KYC verification status
  const fetchVerificationStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Fetching KYC status with token:', token ? 'Present' : 'Missing');
      
      const response = await fetch(API.kycStatus, {
        headers,
      });

      console.log('📡 KYC status response:', response.status, response.statusText);

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ KYC status error:', data);
        throw new Error(data.message || 'Failed to fetch verification status');
      }

      setKycStatus(data);
      
      // If Stripe account exists, create hosted onboarding link
      if (data.provider === 'Stripe' && data.verified === false && data.providerAccountId) {
        setStripeAccountId(data.providerAccountId);
        await createHostedOnboardingLink();
      }
    } catch (err) {
      console.error('Error fetching KYC status:', err);
      
      if (retryCount < 3) {
        console.log(`🔄 Retrying KYC status fetch (attempt ${retryCount + 1}/3)`);
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          fetchVerificationStatus();
        }, 2000);
      } else {
        setError(err.message);
        setRetryCount(0);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  // Create hosted onboarding link
  const createHostedOnboardingLink = async () => {
    try {
      console.log('🔗 Creating hosted onboarding link...');
      
      const response = await fetch(API.stripeCreateAccountLink, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/dashboard/kyc?success=true`,
          refreshUrl: `${window.location.origin}/dashboard/kyc?refresh=true`
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create account link');
      }

      console.log('✅ Hosted onboarding link created:', data.url);
      setAccountLinkUrl(data.url);
    } catch (err) {
      console.error('❌ Error creating hosted onboarding link:', err);
      setError(`Failed to create verification link: ${err.message}`);
    }
  };

  // Clear local state and force account recreation
  const resetLocalState = async () => {
    console.log('🔄 Clearing local state and forcing account recreation...');
    setStripeAccountId(null);
    setAccountLinkUrl(null);
    setSuccess(null);
    setError(null);
    
    // Force refresh the KYC status to clear any cached data
    await fetchVerificationStatus();
  };

  // Start KYC verification with Stripe
  const startStripeKYC = async () => {
    setAccountCreateLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Clear local state to force recreation
      resetLocalState();
      const userEmail = getUserEmailFromToken() || 'user@example.com';
      
      const profileResponse = await fetch(API.userProfile(userId), {
        headers: authHeader
      });
      const profileData = await profileResponse.json();
      const profile = profileData.user || profileData;

      // Get user's country code - try multiple sources
      const userCountry = profile.countryCode || 
                         profile.CountryCode || 
                         profile.countryOfResidence || 
                         profile.CountryOfResidence || 
                         profile.country || 
                         profile.Country || 
                         'GB'; // Default to UK instead of US

      console.log('🌍 User country detected:', userCountry);
      console.log('🌍 Profile country data:', {
        countryCode: profile.countryCode,
        CountryCode: profile.CountryCode,
        countryOfResidence: profile.countryOfResidence,
        CountryOfResidence: profile.CountryOfResidence,
        country: profile.country,
        Country: profile.Country
      });

      const requestBody = {
        providerName: 'Stripe',
        kycMethodId: 1,
        email: profile.email || userEmail,
        country: userCountry,
        type: 'individual',
        forceRecreate: true, // Force creation of new account
        firstName: profile.firstName || profile.FirstName,
        lastName: profile.lastName || profile.LastName,
        phone: profile.mobileNumber || profile.phoneNumber,
        dateOfBirth: profile.dateOfBirth || profile.DateOfBirth,
        address: {
          line1: profile.addressLine1 || profile.AddressLine1,
          city: profile.city || profile.City,
          postal_code: profile.postalCode || profile.PostalCode,
          state: profile.regionId || profile.RegionId,
          country: userCountry
        },
        ssnLast4: profile.ssnLast4 || profile.SsnLast4
      };
      
      console.log('🚀 Starting KYC with:', {
        url: API.kycStart,
        headers: headers,
        body: requestBody
      });
      
      console.log('📋 User profile data being sent:', {
        firstName: requestBody.firstName,
        lastName: requestBody.lastName,
        email: requestBody.email,
        phone: requestBody.phone,
        country: requestBody.country,
        address: requestBody.address
      });

      const response = await fetch(API.kycStart, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.message || data.error || `Failed to start KYC verification (${response.status})`;
        console.error('❌ Detailed error:', errorMessage);
        throw new Error(errorMessage);
      }

      setStripeAccountId(data.accountId);
      setSuccess('KYC verification started! Now complete your verification.');
      
      // Create hosted onboarding link immediately
      await createHostedOnboardingLink();
      
      // Refresh status
      await fetchVerificationStatus();
    } catch (err) {
      console.error('Error starting KYC verification:', err);
      setError(`KYC Error: ${err.message}`);
    } finally {
      setAccountCreateLoading(false);
    }
  };

  // Render status badge
  const renderStatusBadge = () => {
    const statusConfig = {
      not_started: { label: 'Not Started', color: 'default', icon: <PendingIcon /> },
      incomplete: { label: 'Incomplete', color: 'warning', icon: <WarningIcon /> },
      pending: { label: 'Pending', color: 'info', icon: <PendingIcon /> },
      under_review: { label: 'Under Review', color: 'info', icon: <PendingIcon /> },
      verified: { label: 'Verified', color: 'success', icon: <VerifiedIcon /> },
      action_required: { label: 'Action Required', color: 'error', icon: <WarningIcon /> },
    };

    const config = statusConfig[kycStatus.status] || statusConfig.not_started;

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="large"
        sx={{ fontSize: '1rem', py: 2 }}
      />
    );
  };

  // Render current step
  const getCurrentStep = () => {
    if (kycStatus.verified) return 4;
    if (kycStatus.detailsSubmitted) return 3;
    if (kycStatus.status === 'incomplete' || kycStatus.status === 'under_review') return 2;
    if (stripeAccountId) return 1;
    return 0;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1000px', margin: '0 auto' }}>
      <DashboardPageHeader
        icon={VerifiedIcon}
        title="Identity verification (KYC)"
        subtitle="Complete verification to enable withdrawals and payouts securely."
      />

      <Divider sx={{ my: 3 }} />

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }} 
          onClose={() => setError(null)}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => {
                setError(null);
                setRetryCount(0);
                fetchVerificationStatus();
              }}
            >
              Try Again
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Status Card */}
      <Card elevation={3} sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Verification Status</Typography>
            {renderStatusBadge()}
          </Box>

          {/* Stepper */}
          <Stepper activeStep={getCurrentStep()} alternativeLabel sx={{ mt: 3 }}>
            {steps.map((label, index) => (
              <Step key={label} completed={index < getCurrentStep()}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Status Details */}
          <Box mt={3}>
            {kycStatus.verified ? (
              <Alert severity="success" icon={<CheckIcon />}>
                Your identity has been verified! You can now request withdrawals.
              </Alert>
            ) : kycStatus.status === 'under_review' ? (
              <Alert severity="info">
                Your verification is under review. This usually takes 1-2 business days.
              </Alert>
            ) : kycStatus.requirements?.currentlyDue.length > 0 ? (
              <Alert severity="warning">
                <Typography variant="subtitle2" gutterBottom>
                  Action Required:
                </Typography>
                <Typography variant="body2">
                  Please complete the following requirements:
                </Typography>
                <ul>
                  {kycStatus.requirements.currentlyDue.map((req, idx) => (
                    <li key={idx}>{req.replace(/_/g, ' ').toUpperCase()}</li>
                  ))}
                </ul>
              </Alert>
            ) : null}
          </Box>
        </CardContent>
      </Card>

      {/* Action Area */}
      <Paper elevation={2} sx={{ p: 3 }}>
        {kycStatus.verified ? (
          // Step 3: Verification Complete
          <Box textAlign="center">
            <VerifiedIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="success.main" fontWeight="bold">
              Identity Verified!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your identity has been successfully verified. You can now request withdrawals.
            </Typography>
            {kycStatus.verifiedAt && (
              <Typography variant="caption" color="text.secondary">
                Verified on: {new Date(kycStatus.verifiedAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        ) : !stripeAccountId ? (
          // Step 1: Create Stripe Account
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>
              Get Started with Identity Verification
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              We use Stripe to securely verify your identity and enable payments.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={startStripeKYC}
              disabled={accountCreateLoading}
              startIcon={accountCreateLoading ? <CircularProgress size={20} /> : null}
            >
              {accountCreateLoading ? 'Starting Verification...' : 'Start Verification'}
            </Button>
          </Box>
        ) : (
          // Step 2: Complete KYC Verification
          <Box>
            <Typography variant="h6" gutterBottom>
              Complete Your Verification
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please provide the required information to verify your identity.
            </Typography>

            {accountLinkUrl ? (
              <Box mt={3} textAlign="center">
                <Typography variant="h6" gutterBottom>
                  Complete Your Verification
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Click the button below to complete your identity verification in a new window.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => window.open(accountLinkUrl, '_blank')}
                  sx={{ mb: 2 }}
                >
                  Open Verification Form
                </Button>
                <Typography variant="caption" color="text.secondary" display="block">
                  This will open in a new window. Complete the verification and return to this page.
                </Typography>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Preparing verification form...
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={createHostedOnboardingLink}
                  sx={{ mt: 2 }}
                >
                  Create Verification Link
                </Button>
              </Box>
            )}

            <Box mt={3} textAlign="center">
              <Button variant="outlined" onClick={() => {
                setRetryCount(0);
                fetchVerificationStatus();
              }}>
                Refresh Status
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Help Section */}
      <Card elevation={0} sx={{ mt: 3, bgcolor: 'grey.50' }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Why do we need identity verification?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            To comply with financial regulations and prevent fraud, we verify the identity
            of all users who request withdrawals. This is a one-time process.
          </Typography>
          
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            What information do I need?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Government-issued ID (passport, driver's license, etc.)<br />
            • Personal information (name, date of birth, address)<br />
            • Bank account details for withdrawals
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default StripeKYCVerification;